import React, { useEffect, useState, useCallback } from "react";
import { Dialog } from "primereact/dialog";
import ReceiverImageThumbs from "./receiver-image-thumbs";
import ReturnedExtraItemsList from "./returned-extra-items-list";
import { APIGetTBSDetails } from "../../../../service/api-service";
import { GetTBSDetailsResEntity } from "../../../../interface/tbs-tracker/send-tbs";
import {
    buildTbsDetailEntries,
    formatTbsDetailLabel,
    getReceiverAnswerText,
} from "../../../../utils/tbs-verification";

interface ReceiverDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    tbsId?: string;
    tbsNo?: string;
    viewData?: GetTBSDetailsResEntity | null;
}

export default function ReceiverDetailsModal({
    isOpen,
    onClose,
    tbsId,
    tbsNo,
    viewData: propViewData,
}: ReceiverDetailsModalProps) {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [viewData, setViewData] = useState<GetTBSDetailsResEntity | null>(propViewData || null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const fetchTBSDetails = useCallback(async () => {
        if (!tbsId) return;
        setIsLoading(true);
        setErrorMessage(null);
        setViewData(null);

        try {
            const resp = await APIGetTBSDetails(tbsId);
            if (resp && resp.data && resp.status === 200) {
                const apiResp = resp.data;
                if (apiResp && apiResp.statusCode === 200 && apiResp.data) {
                    setViewData(apiResp.data);
                } else {
                    setErrorMessage(
                        apiResp.message || apiResp.errorMsg || "Unable to fetch TBS receiver details."
                    );
                }
            } else {
                setErrorMessage(
                    resp?.data?.message || "Unable to fetch TBS receiver details."
                );
            }
        } catch (err: any) {
            console.error("Error fetching receiver details:", err);
            setErrorMessage(err?.message || "Failed to fetch receiver details.");
        } finally {
            setIsLoading(false);
        }
    }, [tbsId]);

    useEffect(() => {
        if (isOpen) {
            if (propViewData) {
                setViewData(propViewData);
                setErrorMessage(null);
                setIsLoading(false);
            } else if (tbsId) {
                fetchTBSDetails();
            }
        } else {
            setViewData(null);
            setErrorMessage(null);
            setIsLoading(false);
        }
    }, [isOpen, propViewData, tbsId, fetchTBSDetails]);

    const headerTitle = `Receiver Details${
        viewData?.tbsNumber ? ` - #${viewData.tbsNumber}` : tbsNo ? ` - #${tbsNo}` : ""
    }`;

    return (
        <Dialog
            visible={isOpen}
            onHide={onClose}
            header={headerTitle}
            closable={true}
            draggable={false}
            resizable={false}
            style={{ width: "950px", maxWidth: "95vw" }}
            contentStyle={{ maxHeight: "80vh", overflowY: "auto", padding: "1.25rem" }}
        >
            <div>
                {isLoading && (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5">
                        <div
                            className="spinner-border text-primary"
                            role="status"
                            style={{ width: "2.5rem", height: "2.5rem" }}
                        >
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="mt-3 text-muted">Loading receiver details...</div>
                    </div>
                )}

                {!isLoading && errorMessage && (
                    <div className="alert alert-danger d-flex align-items-center justify-content-between p-3 my-3">
                        <div className="d-flex align-items-center">
                            <i className="pi pi-exclamation-circle fs-4 me-2"></i>
                            <span>{errorMessage}</span>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={fetchTBSDetails}
                        >
                            Retry
                        </button>
                    </div>
                )}

                {!isLoading && !errorMessage && (!viewData || !viewData.receiverDetails) && (
                    <div className="text-center py-5">
                        <i
                            className="pi pi-inbox text-muted mb-3"
                            style={{ fontSize: "3rem" }}
                        ></i>
                        <h5 className="text-secondary fw-semibold">No Receiver Details Available</h5>
                        <p className="text-muted mb-0">
                            This TBS has not been verified or received by the store yet.
                        </p>
                    </div>
                )}

                {!isLoading && !errorMessage && viewData && viewData.receiverDetails && (
                    <div>
                        <div className="card border-0 shadow-sm rounded-4 mb-4 bg-light">
                            <div className="card-body p-3">
                                <div className="row g-3">
                                    <div className="col-lg-3 col-md-6">
                                        <div className="fw-medium mb-1">Received By</div>
                                        <small className="text-muted">
                                            {viewData.receiverDetails.receivedBy || "-"}
                                        </small>
                                    </div>
                                    <div className="col-lg-3 col-md-6">
                                        <div className="fw-medium mb-1">Received On</div>
                                        <small className="text-muted">
                                            {viewData.receiverDetails.receivedAt || "-"}
                                        </small>
                                    </div>
                                    <div className="col-lg-3 col-md-6">
                                        <div className="fw-medium mb-1">Received QTY</div>
                                        <small className="text-muted">
                                            {viewData.receiverDetails.qty ?? "-"}
                                        </small>
                                    </div>
                                    <div className="col-lg-3 col-md-6">
                                        <div className="fw-medium mb-1">Receiver Remarks</div>
                                        <small
                                            className="text-muted d-block"
                                            style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                                        >
                                            {viewData.receiverDetails.receivedRemarks || "-"}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card border-0 shadow-sm rounded-4 mb-4">
                            <div className="card-body p-3">
                                <h5 className="card-title fw-bold mb-3">Verification Details</h5>
                                <div className="table-responsive">
                                    <table className="table table-sm table-striped align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>DETAILS</th>
                                                <th>FILLED BY SENDER</th>
                                                <th>VERIFIED BY RECEIVER</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {buildTbsDetailEntries(viewData).map(([field, senderValue]) => (
                                                <tr key={field}>
                                                    <td className="fw-medium">
                                                        {formatTbsDetailLabel(field)}
                                                    </td>
                                                    <td>{senderValue}</td>
                                                    <td>
                                                        {getReceiverAnswerText(
                                                            field,
                                                            senderValue,
                                                            viewData.receiverDetails!
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {viewData.receiverDetails.images && viewData.receiverDetails.images.length > 0 && (
                            <div className="card border-0 shadow-sm rounded-4 mb-4">
                                <div className="card-body p-3">
                                    <h5 className="card-title fw-bold mb-3">Images Of Received / Damaged Items</h5>
                                    <ReceiverImageThumbs urls={viewData.receiverDetails.images} />
                                </div>
                            </div>
                        )}

                        {viewData.returnedItems && viewData.returnedItems.length > 0 && (
                            <div className="card border-0 shadow-sm rounded-4 mb-3">
                                <div className="card-body p-3">
                                    <h5 className="card-title fw-bold mb-1">Returned Items</h5>
                                    <small className="text-muted d-block mb-3">
                                        The receiver did not accept these items. They are no longer
                                        part of this TBS and are pending for a new TBS.
                                    </small>
                                    <div className="table-responsive">
                                        <table className="table table-sm table-striped align-middle mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>ORDER NO.</th>
                                                    <th>PROJECT</th>
                                                    <th>LOCATION NO.</th>
                                                    <th>RETURNED ITEMS</th>
                                                    <th>RETURNED BY</th>
                                                    <th>RETURNED ON</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {viewData.returnedItems.map((item) => (
                                                    <tr key={item._id}>
                                                        <td>{item.orderNumber || "-"}</td>
                                                        <td>{item.projectName || "-"}</td>
                                                        <td>{item.solutionNo || "-"}</td>
                                                        <td>{item.items.join(", ") || "-"}</td>
                                                        <td>{item.returnedBy || "-"}</td>
                                                        <td>{item.returnedAt || "-"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {viewData.extraItemReturns && viewData.extraItemReturns.length > 0 && (
                            <div className="card border-0 shadow-sm rounded-4 mb-3">
                                <div className="card-body p-3">
                                    <h5 className="card-title fw-bold mb-3">Returned Extra Items</h5>
                                    <ReturnedExtraItemsList returns={viewData.extraItemReturns} />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="d-flex justify-content-end mt-4 pt-2 border-top">
                    <button
                        type="button"
                        className="btn btn-primary btn-md font-weight-medium"
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            </div>
        </Dialog>
    );
}
